/**
 * The same change, applied to many transactions at once.
 *
 * Everything here writes in a single SQL transaction and reloads once at the
 * end: a hundred rows edited one by one would be a hundred reloads and a very
 * slow list.
 */
import { run } from '../db'
import { dataChanged } from './live'
import { transactionsByIds } from './transactions'
import { categoryIdFor } from './categories'

async function inTransaction(work) {
  await run('BEGIN')
  try {
    await work()
    await run('COMMIT')
  } catch (err) {
    await run('ROLLBACK')
    throw err
  }
}

export async function deleteTransactions(ids) {
  const list = [...new Set(ids ?? [])]
  if (!list.length) return 0
  await inTransaction(async () => {
    for (const id of list) {
      // Not left to ON DELETE CASCADE: the Capacitor driver does not turn foreign keys on.
      await run('DELETE FROM attachments WHERE transaction_id = ?', [id])
      await run('DELETE FROM transactions WHERE id = ?', [id])
    }
  })
  dataChanged()
  // Its attachments' rows went with it (the foreign key); their bytes go now.
  import('./attachments').then((m) => m.sweep()).catch(() => {})
  return list.length
}

/**
 * @param {string[]} ids
 * @param {object} changes
 * @param {string} [changes.category]  '' takes the category off
 * @param {string} [changes.description]
 * @param {string} [changes.notes]
 * @param {string} [changes.payment_type]
 * @param {string[]} [changes.addTags]
 * @param {string[]} [changes.removeTags]
 * @returns {Promise<{updated: number}>} how many rows were touched
 */
export async function applyBulk(ids, changes = {}) {
  // Only rows that are really there: a selection can outlive a row deleted elsewhere.
  const rows = await transactionsByIds(ids)
  if (!rows.length) return { updated: 0 }
  const stamp = new Date().toISOString()
  // Filing a batch under a category is one of the ways one comes into being.
  const categoryId =
    changes.category === undefined ? undefined : await categoryIdFor(changes.category)

  await inTransaction(async () => {
    for (const tx of rows) {
      const set = { updated_at: stamp }

      if (categoryId !== undefined) set.category_id = categoryId
      if (changes.description !== undefined) set.description = changes.description.trim()
      if (changes.notes !== undefined) set.notes = changes.notes.trim()
      if (changes.payment_type !== undefined) set.payment_type = changes.payment_type.trim()


      const columns = Object.keys(set)
      await run(
        `UPDATE transactions SET ${columns.map((c) => `${c} = ?`).join(', ')} WHERE id = ?`,
        [...columns.map((c) => set[c]), tx.id],
      )

      for (const tagId of changes.addTags ?? []) {
        await run(
          'INSERT OR IGNORE INTO transaction_tags (transaction_id, tag_id) VALUES (?, ?)',
          [tx.id, tagId],
        )
      }
      for (const tagId of changes.removeTags ?? []) {
        await run('DELETE FROM transaction_tags WHERE transaction_id = ? AND tag_id = ?', [
          tx.id,
          tagId,
        ])
      }
    }
  })

  dataChanged()
  return { updated: rows.length }
}
